import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';

const AdminSettings = () => {
  const { settings, fetchSettings } = useSettings();
  const [formData, setFormData] = useState({
    exchange_rate: 40,
    euro_rate: 0,
    payment_phone: '',
    payment_bank: '',
    payment_account: '',
    payment_account_holder: '',
    delivery_fee_usd: 0,
    min_order_usd: 0,
    is_open: true,
    opening_start: '08:00',
    opening_end: '22:00'
  });
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (settings) {
      setFormData({
        exchange_rate: settings.exchange_rate || 40,
        euro_rate: settings.euro_rate || 0,
        payment_phone: settings.payment_phone || '',
        payment_bank: settings.payment_bank || '',
        payment_account: settings.payment_account || '',
        payment_account_holder: settings.payment_account_holder || '',
        delivery_fee_usd: settings.delivery_fee_usd || 0,
        min_order_usd: settings.min_order_usd || 0,
        is_open: settings.is_open !== false,
        opening_start: settings.opening_start || '08:00',
        opening_end: settings.opening_end || '22:00'
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await api.put('/settings', formData);
      await fetchSettings();
      setMessage('✅ Configuración guardada exitosamente');
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      setMessage('❌ Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshRates = async () => {
    setRefreshing(true);
    setMessage('');
    try {
      const { data } = await api.post('/settings/refresh-rates');
      await fetchSettings();
      setMessage('✅ Tasas actualizadas desde DolarAPI');
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      setMessage('❌ Error al actualizar tasas: ' + (error.response?.data?.message || error.message));
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleString('es-VE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold">⚙️ Configuración</h1>
        <p className="text-gray-500 text-sm mt-1">Ajusta la configuración general de tu panadería</p>
      </div>

      {message && (
        <div className={`p-4 rounded-xl mb-6 ${
          message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* ============================================================ */}
      {/* TASAS DE CAMBIO (DOLAR Y EURO) */}
      {/* ============================================================ */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center text-2xl">💱</div>
            <div>
              <h2 className="text-xl font-semibold">Tasas de Cambio</h2>
              <p className="text-sm text-gray-500">Se actualizan automáticamente desde DolarAPI</p>
            </div>
          </div>
          <button
            onClick={handleRefreshRates}
            disabled={refreshing}
            className="px-4 py-2 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition disabled:opacity-50 flex items-center gap-2 text-sm"
          >
            {refreshing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Actualizando...
              </>
            ) : (
              <>🔄 Actualizar ahora</>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Dólar */}
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">💵</span>
              <div>
                <div className="font-semibold text-green-800">Dólar (USD)</div>
                <div className="text-xs text-green-600">Tasa oficial BCV</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                step="0.01"
                name="exchange_rate"
                value={formData.exchange_rate}
                onChange={handleChange}
                className="flex-1 px-4 py-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 text-2xl font-bold text-green-700"
              />
              <span className="text-sm text-green-600 font-medium">Bs/USD</span>
            </div>
            <p className="text-xs text-green-600 mt-2">
              Última actualización: {formatDate(settings?.updated_at)}
            </p>
          </div>

          {/* Euro */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">💶</span>
              <div>
                <div className="font-semibold text-blue-800">Euro (EUR)</div>
                <div className="text-xs text-blue-600">Tasa oficial BCV</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                step="0.01"
                name="euro_rate"
                value={formData.euro_rate}
                onChange={handleChange}
                className="flex-1 px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-2xl font-bold text-blue-700"
              />
              <span className="text-sm text-blue-600 font-medium">Bs/EUR</span>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Tasa del euro para clientes preferenciales
            </p>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
          ℹ️ Las tasas se actualizan automáticamente todos los días a las 6:00 AM (hora de Venezuela) desde DolarAPI.com
        </div>
      </div>

      {/* ============================================================ */}
      {/* RESTO DE LA CONFIGURACIÓN */}
      {/* ============================================================ */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Pago móvil */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-2xl">📱</div>
            <div>
              <h2 className="text-xl font-semibold">Datos de Pago Móvil</h2>
              <p className="text-sm text-gray-500">Información que verán los clientes</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <input type="text" name="payment_phone" value={formData.payment_phone} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" placeholder="04141234567" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Banco</label>
              <input type="text" name="payment_bank" value={formData.payment_bank} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" placeholder="Banesco" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número de Cuenta</label>
              <input type="text" name="payment_account" value={formData.payment_account} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" placeholder="123456789" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Titular</label>
              <input type="text" name="payment_account_holder" value={formData.payment_account_holder} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" placeholder="Dulce 3D C.A." />
            </div>
          </div>
        </div>

        {/* Delivery y horarios */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-2xl">🚚</div>
            <div>
              <h2 className="text-xl font-semibold">Delivery y Horarios</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Costo de envío (USD)</label>
              <input type="number" step="0.01" name="delivery_fee_usd" value={formData.delivery_fee_usd} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pedido mínimo (USD)</label>
              <input type="number" step="0.01" name="min_order_usd" value={formData.min_order_usd} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora de apertura</label>
              <input type="time" name="opening_start" value={formData.opening_start} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora de cierre</label>
              <input type="time" name="opening_end" value={formData.opening_end} onChange={handleChange} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold" />
            </div>
          </div>
          <label className="flex items-center gap-3 mt-4 cursor-pointer p-3 bg-gray-50 rounded-lg">
            <input type="checkbox" name="is_open" checked={formData.is_open} onChange={handleChange} className="w-5 h-5 text-gold rounded focus:ring-gold" />
            <div>
              <div className="font-medium">Tienda abierta</div>
              <div className="text-xs text-gray-500">Los clientes pueden hacer pedidos</div>
            </div>
          </label>
        </div>

        <button type="submit" disabled={saving} className="w-full py-4 bg-gold text-white rounded-full font-bold text-lg hover:bg-gold-dark transition shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
          {saving ? (
            <>
              <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Guardando...
            </>
          ) : (
            <>💾 Guardar toda la configuración</>
          )}
        </button>
      </form>
    </div>
  );
};

export default AdminSettings;
